import React, { useEffect, useState } from "react";
import { getDonors } from "../../../api/GetDonors";

function calcTrend(current, previous) {
  if (previous === 0 && current === 0) return null;
  if (previous === 0) return 100;
  return Math.round(((current - previous) / previous) * 100);
}

function TrendIndicator({ trend }) {
  if (trend === null || trend === undefined) return null;
  const isUp = trend >= 0;
  return (
    <span className={`flex items-center gap-0.5 !font-montserrat !font-semibold !text-[13px] ${isUp ? "text-blue" : "text-red-500"}`}>
      {isUp ? (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 17L17 7M17 7H7M17 7v10" />
        </svg>
      ) : (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 7l10 10M17 17H7M17 17V7" />
        </svg>
      )}
      {Math.abs(trend)}%
    </span>
  );
}

function StatCard({ icon, label, value, trend }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-1">
      <div className="flex items-center gap-2 text-gray-500 !font-montserrat !font-semibold !text-[12px]">
        {icon}
        {label}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-orange !font-poppins !font-semibold !text-[30px] !leading-[40px] !text-left !m-0">{value}</p>
        <TrendIndicator trend={trend} />
      </div>
      {trend !== null && trend !== undefined && (
        <p className="text-black !font-montserrat !text-[12px] !font-normal !m-0 !text-left">compared to last week</p>
      )}
    </div>
  );
}

export default function DashboardBanner({ donations, weeklyDonations = [], prevWeekDonations = [], totalCampaigns, npId, campaigns }) {
  const [donors, setDonors] = useState(0);

  const thisWeekAmount = weeklyDonations.reduce((acc, d) => acc + +d.amount, 0);
  const prevWeekAmount = prevWeekDonations.reduce((acc, d) => acc + +d.amount, 0);
  const donationTrend = calcTrend(thisWeekAmount, prevWeekAmount);

  const displayAmount = parseFloat(thisWeekAmount).toFixed(2).replace(/^0+(\d)/, "$1");

  const getDonorList = async () => {
    try {
      const response = await getDonors(npId);
      if (response.length > 0) {
        setDonors(response.length);
      }
    } catch (error) {
      console.error("Error getting Donors: ", error);
    }
  };

  useEffect(() => {
    if (npId) {
      getDonorList();
    }
  }, [npId]);

  const moneyIcon = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  const peopleIcon = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );

  const campaignIcon = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );

  const tvIcon = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );

  return (
    <div className="grid grid-cols-2 gap-4 h-full">
      <StatCard
        icon={moneyIcon}
        label="Donations"
        value={`$${displayAmount}`}
        trend={donationTrend}
      />
      <StatCard
        icon={peopleIcon}
        label="Total Donors"
        value={donors}
        trend={null}
      />
      <StatCard
        icon={campaignIcon}
        label="Ongoing Campaigns"
        value={campaigns ? campaigns.length : 0}
        trend={null}
      />
      <StatCard
        icon={tvIcon}
        label="Total Campaigns"
        value={totalCampaigns}
        trend={null}
      />
    </div>
  );
}
